import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, CheckCircle2, Clock } from "lucide-react";

/**
 * Clinician "Assign personalised programme" panel (Phase 1B-1, TEST_ONLY).
 * Grants the patient the entitlement to generate a personalised schedule from
 * the clinician-approved master template, and shows the status of anything the
 * patient has already generated. Reuses the existing programme-generator API.
 */
export const GeneratorAssignmentPanel = ({ patientId }) => {
  const [rows, setRows] = useState(null);
  const [assigning, setAssigning] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/programme-generator/assignments/patient/${patientId}`);
      setRows(r.data || []);
    } catch (e) {
      // Non-caseload clinicians are not permitted — hide the panel cleanly.
      setRows([]);
    }
  };
  useEffect(() => { if (patientId) load(); }, [patientId]);

  const assign = async () => {
    setAssigning(true);
    try {
      await api.post("/programme-generator/assignments", { patient_id: patientId });
      toast.success("Personalised programme assigned — the patient can now personalise it.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign programme.");
    } finally { setAssigning(false); }
  };

  const hasActive = (rows || []).some((r) => r.status === "active");

  return (
    <div className="fl-card fl-card-elevated p-6 mt-6" data-testid="generator-assignment-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="text-[#f47a20]" size={18} />
            <div className="fl-heading text-xl">Personalised programme (TEST_ONLY)</div>
          </div>
          <p className="text-sm text-zinc-400 mt-1 max-w-xl">
            Grant this patient the right to generate an individualised schedule from a
            clinician-approved master template. Dosage is never invented and sessions are
            never trimmed — unsupported choices are flagged for your review.
          </p>
        </div>
        <button
          onClick={assign}
          disabled={assigning || hasActive}
          className="fl-btn text-sm"
          data-testid="generator-assign-btn"
        >
          <Plus size={14} /> {hasActive ? "Programme assigned" : assigning ? "Assigning…" : "Assign programme"}
        </button>
      </div>

      {rows === null ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-zinc-500 text-sm" data-testid="generator-assignments-empty">
          No personalised programmes assigned yet.
        </div>
      ) : (
        <div className="divide-y divide-white/5" data-testid="generator-assignments-list">
          {rows.map((a) => (
            <div key={a.id} className="py-3 flex items-start justify-between gap-3" data-testid={`generator-assignment-${a.id}`}>
              <div>
                <div className="text-white text-sm font-semibold">{a.template_title || a.template_slug}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Assigned by {a.assigned_by_name || "clinician"} · {new Date(a.created_at).toLocaleDateString("en-GB")}
                </div>
                {a.generated_programme ? (
                  <div className="mt-2 text-xs text-emerald-300 inline-flex items-center gap-1.5" data-testid="generator-generated-status">
                    <CheckCircle2 size={12} />
                    Generated · {a.generated_programme.completion_frequency || "—"} ·{" "}
                    {a.generated_programme.exercise_count} exercises
                    {a.generated_programme.generated_source?.environment &&
                      ` · ${a.generated_programme.generated_source.environment}`}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-zinc-400 inline-flex items-center gap-1.5" data-testid="generator-pending-status">
                    <Clock size={12} /> Awaiting patient personalisation
                  </div>
                )}
              </div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-zinc-300 shrink-0">
                {a.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GeneratorAssignmentPanel;
